import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { useOutletContext, useSearchParams } from "react-router";
import type { AppOutletContext } from "@/src/layouts/AppLayout";
import { Alert } from "@/components/alert";
import { Button } from "@/components/button";
import { ConfirmDialog } from "@/components/confirm-dialog";
import {
  ConfidenceCard,
  CountryCard,
  FilterCard,
  FilterLabel,
  TimeHorizonPresetsCard,
} from "@/components/filter-cards";
import { SearchMultiSelectWithTags } from "@/components/search-multi-select-with-tags";
import { Icon } from "@/components/icons";
import { PaginationFooter } from "@/components/pagination-footer";
import { Tabs } from "@/components/tabs";
import { TabTransition } from "@/components/tab-transition";
import { Toggle } from "@/components/toggle";
import { Form, FormField, FormSubmit, type FormHelpers } from "@/src/forms";
import { CardForm } from "../_components/card-form";
import { RowActionsMenu } from "../_components/row-actions-menu";
import { SectionHeader } from "../_components/section-header";
import { SectionPage } from "../_components/section-page";
import { AdverseMediaMergeInfoModal } from "../_components/adverse-media-merge-info-modal";
import { EditMemberModal } from "../_components/edit-member-modal";
import { InviteMemberModal } from "../_components/invite-member-modal";
import { DataRetention } from "../_components/data-retention";
import { SkeletonCard } from "../_components/skeleton-card";
import { SearchSettingsChangelog } from "../_components/search-settings-changelog";
import { companyInfoSchema, type CompanyInfoValues } from "../_schemas/company-info";
import { companyAddressSchema, type CompanyAddressValues } from "../_schemas/company-address";
import { accountTypeLabel, MANAGER_ROLES, type AccountType } from "../_data";
import {
  getCompany,
  updateCompany,
  updateCompanyConfig,
  listUsers,
  deleteUser,
  type Company,
  type UserAccount,
} from "@/src/services/company_service";
import { adaptApiInfo, getInfo } from "@/src/services/info_service";
import {
  ADVERSE_MEDIA_COUNTRIES_STRICT_LABEL,
  ADVERSE_MEDIA_KEYWORDS,
  ADVERSE_MEDIA_KEYWORD_LABELS,
  ADVERSE_MEDIA_LANGUAGES,
  ADVERSE_MEDIA_MAX_DAYS_BACK,
  clearCompanyAdverseMedia,
  clearPepSensitivity,
  deleteSearchConfiguration,
  listSearchConfigurations,
  saveCompanyAdverseMedia,
  savePepSensitivity,
} from "@/src/services/search_configurations_service";
import { Controller, useFormState } from "react-hook-form";
import { CountrySelect } from "@/components/country-select";
import { toIsoCode } from "@/app/(app)/_lib/iso-countries";
import { formatDate } from "@/app/(app)/_lib/format";
import { useToast } from "@/components/action-feedback";

const TABS = [
  { id: "information", label: "Information" },
  { id: "adv-media-settings", label: "Search Settings" },
  { id: "accounts-and-sso", label: "Accounts and SSO" },
];

type CompanyTab = "information" | "adv-media-settings" | "accounts-and-sso";

export default function CompanyPage() {
  const ctx = useOutletContext<AppOutletContext | null>();
  const infoPending = !ctx?.infoResolved;
  const canManage =
    infoPending || MANAGER_ROLES.includes((ctx?.apiUser?.accountType ?? "") as AccountType);
  const adverseMediaFeatureEnabled = infoPending || (ctx?.adverseMediaEnabled ?? true);
  const pepFeatureEnabled = infoPending || (ctx?.pepAllowed ?? true);
  const searchSettingsFeatureEnabled = adverseMediaFeatureEnabled || pepFeatureEnabled;
  const canViewAccountsSso = canManage;
  const visibleTabs = TABS.filter((t) => {
    if (t.id === "adv-media-settings") return searchSettingsFeatureEnabled;
    if (t.id === "accounts-and-sso") return canViewAccountsSso;
    return true;
  });

  const [adverseMediaDeleteVersion, setAdverseMediaDeleteVersion] = useState(0);
  const [pepDeleteVersion, setPepDeleteVersion] = useState(0);
  // bumped on every save/delete of a search setting so the changelog refetches the audits
  const [auditsVersion, setAuditsVersion] = useState(0);
  const bumpAudits = useCallback(() => setAuditsVersion((v) => v + 1), []);

  const [params, setParams] = useSearchParams();
  const raw = params.get("tab");
  const tab: CompanyTab =
    (raw === "adv-media-settings" && searchSettingsFeatureEnabled) ||
    (raw === "accounts-and-sso" && canViewAccountsSso)
      ? raw
      : "information";

  function changeTab(id: CompanyTab) {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", id);
        return next;
      },
      { replace: true },
    );
  }

  return (
    <SectionPage title="Company" width="full">
      <Tabs
        tabs={visibleTabs}
        value={tab}
        onChange={(id) => changeTab(id as CompanyTab)}
        className="self-start"
      />
      <TabTransition transitionKey={tab} skipInitial className="flex flex-col gap-2">
        {tab === "information" && <CompanyInformationTab canManage={canManage} />}
        {tab === "adv-media-settings" && searchSettingsFeatureEnabled && (
          <>
            <SearchSettingsChangelog reloadKey={auditsVersion} />
            {adverseMediaFeatureEnabled && (
              <AdverseMediaSettings
                canManage={canManage}
                insetLeft={ctx?.mainInsetLeft}
                pepEnabled={pepFeatureEnabled}
                onChanged={bumpAudits}
                onDeleted={() => setAdverseMediaDeleteVersion((v) => v + 1)}
                peerDeleteVersion={pepDeleteVersion}
              />
            )}
            {pepFeatureEnabled && (
              <PepSensitivitySettings
                canManage={canManage}
                onChanged={bumpAudits}
                onDeleted={() => setPepDeleteVersion((v) => v + 1)}
                peerDeleteVersion={adverseMediaDeleteVersion}
              />
            )}
          </>
        )}
        {tab === "accounts-and-sso" && canViewAccountsSso && (
          <>
            <EnforceSso />
            <Team />
          </>
        )}
      </TabTransition>
    </SectionPage>
  );
}
function CompanyInformationTab({ canManage }: { canManage: boolean }) {
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    getCompany().then((res) => {
      if (cancelled) return;
      if (!res.ok) {
        setLoadError(res.error.message);
        setLoading(false);
        return;
      }
      setLoadError(null);
      setCompany(res.value);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  function retry() {
    setLoadError(null);
    setLoading(true);
    setReloadKey((k) => k + 1);
  }

  if (loading) {
    return (
      <>
        <SkeletonCard heightClass="h-[150px]" />
        <SkeletonCard heightClass="h-[230px]" />
      </>
    );
  }

  if (loadError) {
    return (
      <div className="card flex flex-col gap-3">
        <p className="text-h4 text-black">Couldn&apos;t load your company.</p>
        <p className="text-small text-grey-900">{loadError}</p>
        <div>
          <Button hierarchy="primary" size="sm" onClick={retry}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!company) return null;

  return (
    <>
      <CompanyForms company={company} onSaved={setCompany} canManage={canManage} />
      <DataRetention />
    </>
  );
}
const COMPANY_FIELD_LABELS: Record<string, string> = {
  name: "Company name",
  vat: "VAT ID",
  address_line_1: "Address line 1",
  address_line_2: "Address line 2",
  city: "City",
  postal_code: "Postal code",
  state: "State / Region",
  country: "Country",
};

function describeCompanyErrors(error: {
  message: string;
  fieldErrors?: Record<string, string[]>;
}): string[] {
  const fe = error.fieldErrors;
  if (fe && Object.keys(fe).length > 0) {
    return Object.entries(fe).map(([key, msgs]) => {
      const label = COMPANY_FIELD_LABELS[key];
      return label ? `${label}: ${msgs[0]}` : msgs[0];
    });
  }
  return [error.message];
}

function CompanySaveError({ messages }: { messages: string[] }) {
  return (
    <Alert tone="danger" role="alert" title="Couldn’t save your changes">
      {messages.length === 1 ? (
        <p>{messages[0]}</p>
      ) : (
        <ul className="list-inside list-disc">
          {messages.map((m, i) => (
            <li key={i}>{m}</li>
          ))}
        </ul>
      )}
    </Alert>
  );
}
const companyFormSchema = companyInfoSchema.merge(companyAddressSchema);
type CompanyFormValues = CompanyInfoValues & CompanyAddressValues;

function toCompanyFormValues(c: Company): CompanyFormValues {
  return {
    name: c.name,
    vatId: c.vatId,
    line1: c.addressLine1,
    line2: c.addressLine2,
    city: c.city,
    postalCode: c.postalCode,
    state: c.state,
    country: toIsoCode(c.country),
  };
}
function SectionSubmit({ fields, children }: { fields: string[]; children: ReactNode }) {
  const { dirtyFields } = useFormState<CompanyFormValues>();
  const dirtyMap = dirtyFields as Record<string, unknown>;
  const sectionDirty = fields.some((f) => Boolean(dirtyMap[f]));
  return (
    <FormSubmit hierarchy="primary" pendingLabel="Saving…" disabled={!sectionDirty}>
      {children}
    </FormSubmit>
  );
}

function CompanyForms({
  company,
  onSaved,
  canManage,
}: {
  company: Company;
  onSaved: (c: Company) => void;
  canManage: boolean;
}) {
  const [saved, setSaved] = useState<CompanyFormValues>(() => toCompanyFormValues(company));
  const [formKey, setFormKey] = useState(0);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const showToast = useToast();

  async function handleSave(
    values: CompanyFormValues,
    { setError }: FormHelpers<CompanyFormValues>,
  ) {
    setSaveErrors([]);
    const res = await updateCompany({
      name: values.name,
      vatId: values.vatId,
      addressLine1: values.line1,
      addressLine2: values.line2,
      city: values.city,
      postalCode: values.postalCode,
      state: values.state,
      country: values.country,
    });
    if (!res.ok) {
      const fe = res.error.fieldErrors;
      if (fe?.name?.[0]) setError("name", { message: fe.name[0] });
      if (fe?.vat?.[0]) setError("vatId", { message: fe.vat[0] });
      if (fe?.address_line_1?.[0]) setError("line1", { message: fe.address_line_1[0] });
      if (fe?.address_line_2?.[0]) setError("line2", { message: fe.address_line_2[0] });
      if (fe?.city?.[0]) setError("city", { message: fe.city[0] });
      if (fe?.postal_code?.[0]) setError("postalCode", { message: fe.postal_code[0] });
      if (fe?.state?.[0]) setError("state", { message: fe.state[0] });
      if (fe?.country?.[0]) setError("country", { message: fe.country[0] });
      setSaveErrors(describeCompanyErrors(res.error));
      return;
    }
    setSaved(values);
    setFormKey((k) => k + 1);
    onSaved(res.value);
    showToast({ tone: "success", text: "The company information has been successfully updated." });
  }

  return (
    <Form
      key={formKey}
      schema={companyFormSchema}
      defaultValues={saved}
      onSubmit={handleSave}
      className="flex flex-col gap-2"
    >
      {saveErrors.length > 0 && <CompanySaveError messages={saveErrors} />}
      {!canManage && (
        <p className="text-small text-grey-600">
          Only owners and admins can edit company information.
        </p>
      )}
      <CardForm
        title="Company information"
        description="Used on invoices and shown to your team."
        action={
          canManage ? (
            <SectionSubmit fields={["name", "vatId"]}>Save changes</SectionSubmit>
          ) : undefined
        }
      >
        <div className="grid grid-cols-1 gap-2 @md:grid-cols-2">
          <FormField name="name" label="Company name" disabled={!canManage} />
          <FormField name="vatId" label="VAT ID" disabled={!canManage} />
        </div>
      </CardForm>
      <CardForm
        title="Company address"
        description="Appears on every invoice."
        action={
          canManage ? (
            <SectionSubmit fields={["line1", "line2", "city", "postalCode", "state", "country"]}>
              Save changes
            </SectionSubmit>
          ) : undefined
        }
      >
        <div className="grid grid-cols-1 gap-2 @md:grid-cols-2">
          <FormField
            name="line1"
            label="Address line 1"
            className="@md:col-span-2"
            disabled={!canManage}
          />
          <FormField
            name="line2"
            label="Address line 2"
            className="@md:col-span-2"
            disabled={!canManage}
          />
          <FormField name="city" label="City" disabled={!canManage} />
          <FormField name="postalCode" label="Postal code" disabled={!canManage} />
          <FormField name="state" label="State / Region" disabled={!canManage} />
          <div className="flex flex-col gap-1">
            <label htmlFor="company-country" className="text-small font-medium text-grey-900">
              Country
            </label>
            <Controller
              name="country"
              render={({ field, fieldState }) => (
                <>
                  <CountrySelect
                    id="company-country"
                    value={field.value}
                    onChange={field.onChange}
                    invalid={!!fieldState.error}
                    disabled={!canManage}
                  />
                  {fieldState.error && (
                    <p className="text-small text-red-600">{fieldState.error.message}</p>
                  )}
                </>
              )}
            />
          </div>
        </div>
      </CardForm>
    </Form>
  );
}

const KEYWORD_OPTIONS = ADVERSE_MEDIA_KEYWORDS.map((k) => ({
  value: k,
  name: ADVERSE_MEDIA_KEYWORD_LABELS[k] ?? k,
}));

const ADVERSE_MEDIA_LANGUAGE_LABELS: Record<string, string> = {
  en: "English",
  es: "Spanish",
  fr: "French",
  de: "German",
  pt: "Portuguese",
};
const LANGUAGE_OPTIONS = ADVERSE_MEDIA_LANGUAGES.map((l) => ({
  value: l,
  name: ADVERSE_MEDIA_LANGUAGE_LABELS[l] ?? l,
}));

const DEFAULT_PEP_CONFIDENCE = 88;

async function resolveExistingCompanyConfigId(): Promise<string | null> {
  const list = await listSearchConfigurations();
  if (!list.ok) return null;
  return list.value.find((c) => c.type === "company")?.id ?? null;
}

type AdverseMediaStatus = "loading" | "error" | "ready";
function AdverseMediaSettings({
  canManage,
  insetLeft,
  pepEnabled,
  onChanged,
  onDeleted,
  peerDeleteVersion,
}: {
  canManage: boolean;
  insetLeft?: number;
  pepEnabled: boolean;
  onChanged?: () => void;
  onDeleted?: () => void;
  peerDeleteVersion?: number;
}) {
  const [status, setStatus] = useState<AdverseMediaStatus>("loading");
  const [reloadKey, setReloadKey] = useState(0);
  const [infoModalOpen, setInfoModalOpen] = useState(false);

  const [keywords, setKeywords] = useState<string[]>([]);
  // `null` = strictly defined in the search; `[]` = worldwide (the default).
  const [countries, setCountries] = useState<string[] | null>([]);
  const [languages, setLanguages] = useState<string[]>([]);
  const [baseline, setBaseline] = useState({
    keywords: [] as string[],
    countries: [] as string[] | null,
    languages: [] as string[],
    confidence: 88,
    daysBack: ADVERSE_MEDIA_MAX_DAYS_BACK as number | null,
  });
  const [configId, setConfigId] = useState<string | null>(null);
  const [hasAdverseMedia, setHasAdverseMedia] = useState(false);

  const [confidence, setConfidence] = useState(88);
  const [daysBack, setDaysBack] = useState<number | null>(ADVERSE_MEDIA_MAX_DAYS_BACK);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const showToast = useToast();

  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listSearchConfigurations().then((res) => {
      if (cancelled) return;
      if (!res.ok) {
        setStatus("error");
        return;
      }

      const config =
        res.value.find((c) => c.type === "company" && c.adverseMedia) ??
        res.value.find((c) => c.adverseMedia) ??
        res.value.find((c) => c.type === "company");
      const adverseMedia = config?.adverseMedia;
      const kw = adverseMedia?.keywords ?? [];
      const co = adverseMedia ? adverseMedia.country : [];
      const la = adverseMedia?.language ?? [];
      const cf = adverseMedia?.minScore != null ? Math.round(adverseMedia.minScore * 100) : 88;
      const db = adverseMedia?.daysBack ?? ADVERSE_MEDIA_MAX_DAYS_BACK;
      setKeywords(kw);
      setCountries(co);
      setLanguages(la);
      setConfidence(cf);
      setDaysBack(db);
      setBaseline({ keywords: kw, countries: co, languages: la, confidence: cf, daysBack: db });
      setConfigId(config?.id ?? null);
      setHasAdverseMedia(!!adverseMedia);
      setStatus("ready");
    });
    return () => {
      cancelled = true;
    };
  }, [reloadKey, peerDeleteVersion]);

  const dirty =
    JSON.stringify({ keywords, countries, languages, confidence, daysBack }) !==
    JSON.stringify(baseline);

  function edit<T>(setter: (v: T) => void) {
    return setter;
  }

  async function handleSave(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!dirty || saving) return;
    setSaving(true);
    setSaveError(null);

    const targetId = configId ?? (await resolveExistingCompanyConfigId());
    const res = await saveCompanyAdverseMedia(
      { countries, languages, keywords, confidenceScore: confidence, daysBack },
      targetId ?? undefined,
    );
    if (!res.ok) {
      setSaving(false);
      setSaveError(res.error.message);
      return;
    }

    setConfigId(res.value.id ?? targetId);

    const adverseMedia = res.value.adverseMedia;
    const savedConfidence =
      adverseMedia?.minScore != null ? Math.round(adverseMedia.minScore * 100) : confidence;
    const next = {
      keywords: adverseMedia?.keywords ?? keywords,
      countries: adverseMedia ? adverseMedia.country : countries,
      languages: adverseMedia?.language ?? languages,
      confidence: savedConfidence,
      daysBack: adverseMedia?.daysBack ?? daysBack,
    };
    setKeywords(next.keywords);
    setCountries(next.countries);
    setLanguages(next.languages);
    setConfidence(next.confidence);
    setDaysBack(next.daysBack);
    setBaseline(next);
    setHasAdverseMedia(!!adverseMedia);
    setSaving(false);
    onChanged?.();
    showToast({ tone: "success", text: "Adverse media settings saved successfully." });
  }

  function handleCancel() {
    setKeywords(baseline.keywords);
    setCountries(baseline.countries);
    setLanguages(baseline.languages);
    setConfidence(baseline.confidence);
    setDaysBack(baseline.daysBack);
    setSaveError(null);
  }

  async function handleDelete() {
    if (!configId || deleting) return;
    setDeleting(true);
    setDeleteError(null);
    const list = await listSearchConfigurations();
    const sharesRowWithPep = !list.ok || !!list.value.find((c) => c.id === configId)?.pep;
    const res = sharesRowWithPep
      ? await clearCompanyAdverseMedia(configId)
      : await deleteSearchConfiguration("company", configId);
    if (!res.ok) {
      setDeleting(false);
      setDeleteError(res.error.message);
      return;
    }
    const empty = {
      keywords: [],
      countries: [] as string[] | null,
      languages: [],
      confidence: 88,
      daysBack: ADVERSE_MEDIA_MAX_DAYS_BACK as number | null,
    };
    setKeywords(empty.keywords);
    setCountries(empty.countries);
    setLanguages(empty.languages);
    setConfidence(empty.confidence);
    setDaysBack(empty.daysBack);
    setBaseline(empty);
    setHasAdverseMedia(false);
    setConfigId(sharesRowWithPep ? configId : null);
    setSaveError(null);
    setDeleting(false);
    setConfirmingDelete(false);
    onChanged?.();
    onDeleted?.();
    showToast({ tone: "success", text: "Adverse media settings deleted successfully." });
  }

  if (status === "loading") return <SkeletonCard heightClass="h-[260px]" />;

  if (status === "error") {
    return (
      <div className="card flex flex-col gap-3">
        <p className="text-h4 text-black">Couldn&apos;t load your adverse media settings.</p>
        <p className="text-small text-grey-900">
          We couldn&apos;t reach your saved defaults. Check your connection and try again.
        </p>
        <div>
          <Button
            hierarchy="primary"
            size="sm"
            onClick={() => {
              setStatus("loading");
              setReloadKey((k) => k + 1);
            }}
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSave} className="@container flex flex-col gap-2">
      <SectionHeader
        title="Adverse media settings"
        description="Parameters below will be used as the default for new screenings."
        titleInfo={
          <button
            type="button"
            onClick={() => setInfoModalOpen(true)}
            aria-label="How merge and override work"
            className="flex size-5 items-center justify-center text-grey-500 transition-colors hover:text-grey-900"
          >
            <Icon name="info" size={20} filled />
          </button>
        }
      />
      <p className="border-l-2 border-grey-200 pl-3 text-small text-grey-900">
        Keywords, countries, and languages are merged with whatever a search request or API call
        specifies — your company settings are never removed, only added to. Choosing &ldquo;
        {ADVERSE_MEDIA_COUNTRIES_STRICT_LABEL}&rdquo; for countries skips that merge and uses only
        the countries sent in the search request. The confidence score and time range always take
        priority over any value sent in a search request, monitoring entry, or API request.
      </p>
      <AdverseMediaMergeInfoModal
        open={infoModalOpen}
        onClose={() => setInfoModalOpen(false)}
        insetLeft={insetLeft}
      />

      <div className="card @container flex flex-col gap-2">
        {saveError && canManage && (
          <Alert tone="danger" role="alert">
            {saveError}
          </Alert>
        )}
        {!canManage && (
          <p className="text-small text-grey-600">
            Only owners and admins can edit adverse media settings.
          </p>
        )}
        <div className="grid grid-cols-1 items-start gap-2 @md:grid-cols-2">
          <ConfidenceCard
            label="Adverse media confidence score"
            value={confidence}
            onChange={setConfidence}
            className="h-20 w-full justify-center"
            disabled={!canManage}
            labelSize="small"
          />
          <FilterCard disabled={!canManage}>
            <FilterLabel size="small">Adverse media keywords</FilterLabel>
            <SearchMultiSelectWithTags
              items={KEYWORD_OPTIONS}
              selected={keywords}
              onChange={edit(setKeywords)}
              placeholder="Search keywords"
              allSelector={{ name: "All" }}
              disabled={!canManage}
            />
          </FilterCard>
          <CountryCard
            label="Adverse media countries"
            selected={countries}
            onChange={edit(setCountries)}
            className="flex w-full flex-col gap-1"
            allSelector={true}
            strictSelector
            disabled={!canManage}
            labelSize="small"
          />
          <FilterCard disabled={!canManage}>
            <FilterLabel size="small">Adverse media languages</FilterLabel>
            <SearchMultiSelectWithTags
              items={LANGUAGE_OPTIONS}
              selected={languages}
              onChange={edit(setLanguages)}
              placeholder="Search languages"
              allSelector={{ name: "All" }}
              disabled={!canManage}
            />
          </FilterCard>
          <TimeHorizonPresetsCard
            value={daysBack}
            onChange={setDaysBack}
            disabled={!canManage}
            labelSize="small"
          />
        </div>

        {canManage && (
          <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => {
                setDeleteError(null);
                setConfirmingDelete(true);
              }}
              disabled={!hasAdverseMedia || saving || deleting}
              className="inline-flex items-center gap-1 text-body font-medium text-red-600 transition-colors hover:text-red-700 disabled:cursor-not-allowed disabled:text-grey-300"
            >
              <Icon name="delete" size={20} />
              Delete
            </button>
            <Button
              type="button"
              hierarchy="secondary"
              onClick={handleCancel}
              disabled={!dirty || saving}
              className="shrink-0"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              hierarchy="primary"
              disabled={!dirty || saving}
              className="shrink-0"
            >
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirmingDelete}
        onClose={() => setConfirmingDelete(false)}
        onConfirm={() => void handleDelete()}
        title="Delete adverse media settings?"
        description="Your company's default adverse media keywords, countries and languages will be removed. New screenings will fall back to the system defaults."
        confirmLabel="Delete settings"
        busyLabel="Deleting…"
        busy={deleting}
        error={deleteError ?? undefined}
        destructive
      />
    </form>
  );
}

type PepSensitivityStatus = "loading" | "error" | "ready";
function PepSensitivitySettings({
  canManage,
  onChanged,
  onDeleted,
  peerDeleteVersion,
}: {
  canManage: boolean;
  onChanged?: () => void;
  onDeleted?: () => void;
  peerDeleteVersion?: number;
}) {
  const [status, setStatus] = useState<PepSensitivityStatus>("loading");
  const [reloadKey, setReloadKey] = useState(0);

  const [confidence, setConfidence] = useState(DEFAULT_PEP_CONFIDENCE);
  const [baseline, setBaseline] = useState(DEFAULT_PEP_CONFIDENCE);
  const [hasOverride, setHasOverride] = useState(false);
  const [configId, setConfigId] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const showToast = useToast();

  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listSearchConfigurations().then((res) => {
      if (cancelled) return;
      if (!res.ok) {
        setStatus("error");
        return;
      }

      const config =
        res.value.find((c) => c.type === "company" && c.pep) ??
        res.value.find((c) => c.pep) ??
        res.value.find((c) => c.type === "company");
      const pep = config?.pep;
      const cf = pep?.minScore != null ? Math.round(pep.minScore * 100) : DEFAULT_PEP_CONFIDENCE;
      setConfidence(cf);
      setBaseline(cf);
      setHasOverride(pep?.minScore != null);
      setConfigId(config?.id ?? null);
      setStatus("ready");
    });
    return () => {
      cancelled = true;
    };
  }, [reloadKey, peerDeleteVersion]);

  const dirty = confidence !== baseline;

  async function handleSave(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!dirty || saving) return;
    setSaving(true);
    setSaveError(null);

    const targetId = configId ?? (await resolveExistingCompanyConfigId());
    const res = await savePepSensitivity({ confidenceScore: confidence }, targetId ?? undefined);
    if (!res.ok) {
      setSaving(false);
      setSaveError(res.error.message);
      return;
    }

    setConfigId(res.value.id ?? targetId);
    const savedConfidence =
      res.value.pep?.minScore != null ? Math.round(res.value.pep.minScore * 100) : confidence;
    setConfidence(savedConfidence);
    setBaseline(savedConfidence);
    setHasOverride(res.value.pep?.minScore != null);
    setSaving(false);
    onChanged?.();
    showToast({ tone: "success", text: "PEP settings saved successfully." });
  }

  function handleCancel() {
    setConfidence(baseline);
    setSaveError(null);
  }

  async function handleDelete() {
    if (!configId || deleting) return;
    setDeleting(true);
    setDeleteError(null);
    const list = await listSearchConfigurations();
    const sharesRowWithAdverseMedia =
      !list.ok || !!list.value.find((c) => c.id === configId)?.adverseMedia;
    const res = sharesRowWithAdverseMedia
      ? await clearPepSensitivity(configId)
      : await deleteSearchConfiguration("company", configId);
    if (!res.ok) {
      setDeleting(false);
      setDeleteError(res.error.message);
      return;
    }
    setConfidence(DEFAULT_PEP_CONFIDENCE);
    setBaseline(DEFAULT_PEP_CONFIDENCE);
    setHasOverride(false);
    setConfigId(sharesRowWithAdverseMedia ? configId : null);
    setSaveError(null);
    setDeleting(false);
    setConfirmingDelete(false);
    onChanged?.();
    onDeleted?.();
    showToast({ tone: "success", text: "PEP settings deleted successfully." });
  }

  if (status === "loading") return <SkeletonCard heightClass="h-[180px]" />;

  if (status === "error") {
    return (
      <div className="card flex flex-col gap-3">
        <p className="text-h4 text-black">Couldn&apos;t load your PEP settings.</p>
        <p className="text-small text-grey-900">
          We couldn&apos;t reach your saved defaults. Check your connection and try again.
        </p>
        <div>
          <Button
            hierarchy="primary"
            size="sm"
            onClick={() => {
              setStatus("loading");
              setReloadKey((k) => k + 1);
            }}
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSave} className="@container flex flex-col gap-2">
      <SectionHeader
        title="PEP settings"
        description="The score below will be used as the default for new PEP searches."
      />
      <p className="border-l-2 border-grey-200 pl-3 text-small text-grey-900">
        The PEP match confidence score always takes priority over any value sent in a search
        request, monitoring entry, or API request. It only raises the bar for Politically Exposed
        Persons (PEP) results — it doesn&apos;t turn PEP screening on or off, and it can&apos;t
        loosen matching below the platform default.
      </p>

      <div className="card @container flex flex-col gap-2">
        {saveError && canManage && (
          <Alert tone="danger" role="alert">
            {saveError}
          </Alert>
        )}
        {!canManage && (
          <p className="text-small text-grey-600">Only owners and admins can edit PEP settings.</p>
        )}
        <div className="grid grid-cols-1 items-start gap-2 @md:grid-cols-2">
          <ConfidenceCard
            label="PEP match confidence score"
            value={confidence}
            onChange={setConfidence}
            className="h-20 w-full justify-center"
            disabled={!canManage}
            labelSize="small"
          />
        </div>

        {canManage && (
          <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => {
                setDeleteError(null);
                setConfirmingDelete(true);
              }}
              disabled={!hasOverride || saving || deleting}
              className="inline-flex items-center gap-1 text-body font-medium text-red-600 transition-colors hover:text-red-700 disabled:cursor-not-allowed disabled:text-grey-300"
            >
              <Icon name="delete" size={20} />
              Delete
            </button>
            <Button
              type="button"
              hierarchy="secondary"
              onClick={handleCancel}
              disabled={!dirty || saving}
              className="shrink-0"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              hierarchy="primary"
              disabled={!dirty || saving}
              className="shrink-0"
            >
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirmingDelete}
        onClose={() => setConfirmingDelete(false)}
        onConfirm={() => void handleDelete()}
        title="Delete PEP settings?"
        description="Your company's custom PEP match confidence score will be removed. New screenings will use the platform's default PEP threshold."
        confirmLabel="Delete settings"
        busyLabel="Deleting…"
        busy={deleting}
        error={deleteError ?? undefined}
        destructive
      />
    </form>
  );
}

type EnforceSsoStatus = "loading" | "error" | "ready";
function EnforceSso() {
  const [status, setStatus] = useState<EnforceSsoStatus>("loading");
  const [enforceSso, setEnforceSso] = useState(false);
  const [canManage, setCanManage] = useState(false);
  const [userSsoEnabled, setUserSsoEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const showToast = useToast();

  useEffect(() => {
    let cancelled = false;
    getInfo()
      .then(adaptApiInfo)
      .then(
        (info) => {
          if (cancelled) return;
          setEnforceSso(info.company?.enforceSso ?? false);
          setCanManage(MANAGER_ROLES.includes(info.user.accountType as AccountType));
          setUserSsoEnabled(info.user.ssoEnabled);
          setStatus("ready");
        },
        () => {
          if (!cancelled) setStatus("error");
        },
      );
    return () => {
      cancelled = true;
    };
  }, []);

  async function apply(next: boolean) {
    setSaving(true);
    setSaveError(null);
    const res = await updateCompanyConfig({ enforceSso: next });
    setSaving(false);
    if (!res.ok) {
      setSaveError(res.error.message);
      return;
    }
    setEnforceSso(res.value.enforceSso);
    setConfirmOpen(false);
    showToast({
      tone: "success",
      text: next ? "SSO is now enforced company-wide." : "SSO enforcement has been turned off.",
    });
  }

  function handleToggle(e: React.ChangeEvent<HTMLInputElement>) {
    if (!e.target.checked) {
      void apply(false);
      return;
    }
    setSaveError(null);
    setConfirmOpen(true);
  }

  if (status === "loading") return <SkeletonCard heightClass="h-[88px]" />;

  const locked = !canManage || (!userSsoEnabled && !enforceSso);

  return (
    <div className="card flex flex-col gap-2">
      <div className="flex items-center gap-4">
        <div className="flex size-12 shrink-0 items-center justify-center rounded-sm bg-green-200 text-green-700">
          <Icon name="key" size={24} />
        </div>
        <div className="flex flex-1 flex-col gap-0.5">
          <span className="text-body font-semibold text-black">Enforce SSO</span>
          <span className="text-small text-grey-900">
            {status === "error"
              ? "Couldn’t load this setting. Refresh the page to try again."
              : !canManage
                ? "Only owners and admins can change this."
                : !userSsoEnabled && !enforceSso
                  ? "Connect a single sign-on provider on your own account (Account settings) before enforcing it company-wide."
                  : "All members must sign in through a connected identity provider. Email + password sign-in is disabled."}
          </span>
        </div>
        <Toggle
          checked={enforceSso}
          onChange={handleToggle}
          disabled={status === "error" || locked || saving}
          slotProps={{ input: { "aria-label": "Enforce SSO" } }}
        />
      </div>

      {saveError && !confirmOpen && (
        <p
          role="alert"
          className="rounded-[4px] border border-red-500 bg-red-100 px-3 py-2 text-small text-red-600"
        >
          {saveError}
        </p>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setSaveError(null);
        }}
        onConfirm={() => void apply(true)}
        title="Enforce SSO"
        description="Everyone in your company will be required to sign in through a connected identity provider — existing passwords are deleted and email/password sign-in stops working immediately."
        confirmLabel="Enforce SSO"
        busyLabel="Enabling…"
        busy={saving}
        error={saveError ?? undefined}
        destructive
      />
    </div>
  );
}

type TeamLoadStatus = "loading" | "error" | "ready";
const TEAM_PAGE_SIZE = 25;
function Team() {
  const [users, setUsers] = useState<UserAccount[]>([]);
  const [count, setCount] = useState(0);
  const [status, setStatus] = useState<TeamLoadStatus>("loading");
  const [currentUser, setCurrentUser] = useState<{ uuid: string; accountType: string } | null>(
    null,
  );
  const [actionError, setActionError] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editing, setEditing] = useState<UserAccount | null>(null);
  const [pendingRemove, setPendingRemove] = useState<UserAccount | null>(null);
  const [page, setPage] = useState(1);
  const [reloadKey, setReloadKey] = useState(0);
  const showToast = useToast();

  const reload = useCallback(() => {
    setPage(1);
    setReloadKey((k) => k + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    getInfo()
      .then(adaptApiInfo)
      .then(
        (info) => {
          if (!cancelled) {
            setCurrentUser({ uuid: info.user.uuid, accountType: info.user.accountType });
          }
        },
        () => {},
      );
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    listUsers(page, TEAM_PAGE_SIZE).then((usersRes) => {
      if (cancelled) return;
      if (!usersRes.ok) {
        setStatus("error");
        return;
      }
      setUsers(usersRes.value.users);
      setCount(usersRes.value.count);
      setStatus("ready");
    });
    return () => {
      cancelled = true;
    };
  }, [reloadKey, page]);

  const canManage = !!currentUser && MANAGER_ROLES.includes(currentUser.accountType as AccountType);

  const pageCount = Math.max(1, Math.ceil(count / TEAM_PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const rangeStart = count === 0 ? 0 : (safePage - 1) * TEAM_PAGE_SIZE + 1;
  const rangeEnd = (safePage - 1) * TEAM_PAGE_SIZE + users.length;

  async function confirmRemove() {
    const target = pendingRemove;
    setPendingRemove(null);
    if (!target) return;
    setActionError(null);
    const res = await deleteUser(target.uuid);
    if (!res.ok) {
      setActionError(res.error.message);
      return;
    }
    reload();
    showToast({ tone: "success", text: "The user has been successfully deleted." });
  }

  return (
    <div className="card @container flex flex-col gap-4">
      <SectionHeader
        title="Company accounts"
        description="People who can access this workspace."
        action={
          canManage ? (
            <Button
              hierarchy="primary"
              onClick={() => setInviteOpen(true)}
              leftIcon={<Icon name="add" size={20} />}
            >
              Invite user
            </Button>
          ) : undefined
        }
      />

      {actionError && (
        <Alert tone="danger" role="alert">
          {actionError}
        </Alert>
      )}

      {status === "loading" ? (
        <TeamSkeleton />
      ) : status === "error" ? (
        <TeamError onRetry={reload} />
      ) : users.length === 0 ? (
        <div className="rounded-sm border border-dashed border-grey-200 px-3 py-6 text-center text-small text-grey-900">
          No team members yet.
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <div className="overflow-x-auto rounded-sm border border-grey-200">
            <table className="w-full text-small">
              <thead className="bg-grey-50">
                <tr className="text-left text-grey-900">
                  <Th>Name</Th>
                  <Th>Email</Th>
                  <Th>Role</Th>
                  <Th>Date joined</Th>
                  <Th>Last login</Th>
                  <Th>Status</Th>
                  <Th className="text-center">Monitoring</Th>
                  <Th className="text-center">SSO</Th>
                  <Th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const fullName = `${u.firstName} ${u.lastName}`.trim();
                  const isSelf = currentUser?.uuid === u.uuid;
                  return (
                    <tr key={u.uuid} className="border-t border-grey-200">
                      <Td className="font-medium text-black">{fullName || "—"}</Td>
                      <Td className="text-grey-900">{u.email}</Td>
                      <Td className="text-grey-900">{accountTypeLabel(u.accountType)}</Td>
                      <Td className="whitespace-nowrap text-grey-900">
                        {u.dateJoined ? formatDate(new Date(u.dateJoined)) : "—"}
                      </Td>
                      <Td className="whitespace-nowrap text-grey-900">
                        {u.lastLogin ? formatDate(new Date(u.lastLogin)) : "Never"}
                      </Td>
                      <Td>
                        <StatusBadge active={u.isActive} />
                      </Td>
                      <Td className="text-center">
                        <CheckDot on={u.enableMonitoring} />
                      </Td>
                      <Td className="text-center">
                        <CheckDot on={u.ssoEnabled} />
                      </Td>
                      <Td className="text-right">
                        {canManage && !isSelf ? (
                          <RowActionsMenu
                            label={`Actions for ${fullName || u.email}`}
                            onEdit={() => setEditing(u)}
                            onRemove={() => setPendingRemove(u)}
                          />
                        ) : null}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="px-1 text-tiny text-grey-900">
            Showing {rangeStart}–{rangeEnd} of {count} {count === 1 ? "member" : "members"}
          </p>
          {pageCount > 1 && (
            <PaginationFooter
              compact
              total={count}
              individuals={0}
              organizations={0}
              others={0}
              page={safePage}
              pageCount={pageCount}
              onPageChange={setPage}
            />
          )}
        </div>
      )}

      <InviteMemberModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onCreated={() => {
          reload();
          showToast({ tone: "success", text: "The user has been successfully invited." });
        }}
        actorRole={(currentUser?.accountType as AccountType) ?? "auditor"}
      />
      <EditMemberModal
        open={editing !== null}
        onClose={() => setEditing(null)}
        member={editing}
        onSaved={() => {
          reload();
          showToast({
            tone: "success",
            text: "The user's information has been successfully updated.",
          });
        }}
        onRemove={(uuid) => {
          const u = users.find((x) => x.uuid === uuid);
          if (u) setPendingRemove(u);
        }}
        actorRole={(currentUser?.accountType as AccountType) ?? "auditor"}
      />

      <ConfirmDialog
        open={pendingRemove !== null}
        onClose={() => setPendingRemove(null)}
        onConfirm={() => void confirmRemove()}
        title={
          pendingRemove
            ? `Remove ${`${pendingRemove.firstName} ${pendingRemove.lastName}`.trim() || pendingRemove.email}?`
            : ""
        }
        description="They'll lose access to the workspace immediately and will need a new invite to rejoin."
        confirmLabel="Remove member"
        destructive
      />
    </div>
  );
}

function TeamSkeleton() {
  return (
    <div
      className="flex flex-col gap-px overflow-hidden rounded-sm border border-grey-200"
      aria-busy="true"
    >
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="h-12 w-full animate-pulse bg-grey-50" />
      ))}
    </div>
  );
}

function TeamError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-sm border border-grey-200 bg-white px-4 py-8 text-center">
      <div className="flex size-9 items-center justify-center rounded-sm bg-red-100 text-red-600">
        <Icon name="error" size={20} />
      </div>
      <p className="text-small text-grey-900">Couldn’t load your team.</p>
      <Button
        hierarchy="secondary"
        size="sm"
        leftIcon={<Icon name="refresh" size={20} />}
        onClick={onRetry}
      >
        Retry
      </Button>
    </div>
  );
}

function CheckDot({ on }: { on: boolean }) {
  return on ? (
    <Icon name="check_circle" size={20} filled className="text-green-500" />
  ) : (
    <span className="inline-block size-2 rounded-full bg-grey-200" />
  );
}

function Th({ children, className = "", ...rest }: React.ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th className={`whitespace-nowrap px-3 py-2 text-small font-medium ${className}`} {...rest}>
      {children}
    </th>
  );
}

function Td({ children, className = "", ...rest }: React.TdHTMLAttributes<HTMLTableCellElement>) {
  return (
    <td className={`px-3 py-2 ${className}`} {...rest}>
      {children}
    </td>
  );
}
function StatusBadge({ active }: { active: boolean }) {
  return (
    <span
      className={`inline-flex items-center rounded-sm px-2 py-0.5 text-tiny font-medium ${
        active ? "bg-green-200 text-green-900" : "bg-grey-100 text-grey-900"
      }`}
    >
      {active ? "Active" : "Invited"}
    </span>
  );
}
